/**
 * Zod schema for the product editor form (EPIC-006 Phase 7, Task 7.2).
 *
 * Mirrors the backend Pydantic validators (`PricingFields` /
 * `SellerProductCreate` / `SellerProductUpdate`):
 *   - `price_kgs`: REQUIRED, > 0 (Decimal KGS — kept as a string in the form
 *     so no float math happens before the API boundary). SCRUM-167b: the
 *     Pydantic field TYPE is nullable (`Decimal | None`), but the SERVICE
 *     layer (`_validate_product_business_rules`) enforces
 *     `marketplace.product.price_required` unconditionally on both create
 *     AND update — a blank price 422s server-side regardless of what the
 *     type permits. This schema mirrors the service rule, not the bare type
 *     (a comment that used to claim the opposite is exactly how this bug
 *     shipped — the client silently allowed what the server always refused).
 *   - `bulk_price_kgs` stays genuinely OPTIONAL (null OR > 0) and is PAIRED
 *     with `bulk_threshold` (both-or-neither); `bulk_threshold` must be > 1.
 *   - `bulk_price_kgs` must be strictly BELOW `price_kgs` when both are set.
 *
 * Prices live in the form as raw strings (e.g. "1234.56"); a thin parse helper
 * coerces to a number ONLY for the comparison checks. The empty string means
 * "unset" → serialised to `null`.
 */
import { z } from "zod";

/** Error keys map to `editor.pricing.errors.*` in the i18n namespace. */
export const PRICING_ERROR_KEYS = {
  priceRequired: "editor.pricing.errors.priceRequired",
  pricePositive: "editor.pricing.errors.pricePositive",
  bulkPair: "editor.pricing.errors.bulkPair",
  bulkBelowUnit: "editor.pricing.errors.bulkBelowUnit",
  thresholdMin: "editor.pricing.errors.thresholdMin",
} as const;

/** Parse a price string to a number; "" / null → null; invalid → NaN. */
export function parsePriceInput(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  // Accept both "1234.56" and "1234,56" (RU comma) — normalise to a dot.
  const normalised = trimmed.replace(/\s/g, "").replace(",", ".");
  const n = Number(normalised);
  return Number.isFinite(n) ? n : NaN;
}

/** A non-empty, > 0 price string, or "" for unset — used ONLY by
 * `bulk_price_kgs`, which genuinely is optional server-side. */
const priceStringSchema = z
  .string()
  .refine(
    (v) => {
      const n = parsePriceInput(v);
      if (n === null) return true; // unset is allowed
      return Number.isFinite(n) && n > 0;
    },
    { message: PRICING_ERROR_KEYS.pricePositive },
  );

/** SCRUM-167b: a REQUIRED, > 0 price string — used by `price_kgs` on both
 * the seller and admin editors (they share this schema; the server 422s a
 * blank price on both surfaces, create AND update, so the client must
 * refuse it before the request is ever sent). Two distinct failure modes
 * (blank vs. non-positive) carry two distinct message keys. */
const requiredPriceStringSchema = z.string().superRefine((v, ctx) => {
  const n = parsePriceInput(v);
  if (n === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: PRICING_ERROR_KEYS.priceRequired,
    });
    return;
  }
  if (!Number.isFinite(n) || n <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: PRICING_ERROR_KEYS.pricePositive,
    });
  }
});

/** Canonical unit-of-sale slugs, mirroring the API's PRODUCT_UNIT_VALUES.
 * The API rejects anything outside this set, so the form must not offer it. */
export const PRODUCT_UNIT_SLUGS = [
  "pcs",
  "set",
  "m",
  "m2",
  "m3",
  "kg",
  "l",
  "bag",
  "pack",
  "roll",
] as const;

export const productEditorSchema = z
  .object({
    name: z.string().min(1).max(300),
    description: z.string().max(5000),
    category_id: z.string().min(1),
    status: z.string().min(1),
    stock_state: z.enum(["in_stock", "low", "out_of_stock", "unknown"]),
    price_kgs: requiredPriceStringSchema,
    bulk_price_kgs: priceStringSchema,
    bulk_threshold: z.string(),
    // v0048 workbook properties. `unit` is a CLOSED set — the API 422s an
    // unknown slug because a wrong unit misprices the product — so the form
    // offers a select and "" means "not stated".
    unit: z.enum(["", ...PRODUCT_UNIT_SLUGS]),
    brand: z.string().max(200),
    article: z.string().max(120),
    formal_name: z.string().max(300),
  })
  .superRefine((data, ctx) => {
    const price = parsePriceInput(data.price_kgs);
    const bulkPrice = parsePriceInput(data.bulk_price_kgs);
    const thresholdRaw = data.bulk_threshold.trim();
    const hasThreshold = thresholdRaw !== "";
    const threshold = hasThreshold ? Number(thresholdRaw) : null;
    const hasBulkPrice = bulkPrice !== null && !Number.isNaN(bulkPrice);

    // Pairing: both-or-neither.
    if (hasBulkPrice !== hasThreshold) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: PRICING_ERROR_KEYS.bulkPair,
        path: ["bulk_price_kgs"],
      });
    }
    // Threshold must be > 1.
    if (hasThreshold && (threshold === null || !Number.isInteger(threshold) || threshold <= 1)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: PRICING_ERROR_KEYS.thresholdMin,
        path: ["bulk_threshold"],
      });
    }
    // Bulk price must be strictly below the unit price.
    if (
      hasBulkPrice &&
      price !== null &&
      !Number.isNaN(price) &&
      bulkPrice !== null &&
      bulkPrice >= price
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: PRICING_ERROR_KEYS.bulkBelowUnit,
        path: ["bulk_price_kgs"],
      });
    }
  });

export type ProductEditorForm = z.infer<typeof productEditorSchema>;

/** One variant row in the editor (both fields required, non-empty). */
export interface VariantRow {
  /** Server id when persisted; null for a not-yet-saved row. */
  id: string | null;
  key: string;
  value: string;
}
